import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Copy, Check, Facebook, Send } from "lucide-react";
import { toast } from "sonner";

const MESSAGE = "🐆 I'm on Spotter — the social network where personality matters. Forget your stripes, come earn your spots with me!";

export function InviteDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/?ref=${user?.id}`;
  const full = `${MESSAGE}\n${link}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(full); setCopied(true); toast.success("Invite copied! Paste it anywhere."); setTimeout(() => setCopied(false), 2000); }
    catch (e) { toast.error("Couldn't copy — select the link manually."); }
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Join me on Spotter", text: MESSAGE, url: link }); } catch (e) {} }
    else copy();
  };
  const enc = encodeURIComponent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-4 border-black bg-spotter-cream max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><UserPlus className="w-6 h-6" style={{ color: "#E19C00" }} />Invite friends, earn spots</DialogTitle>
          <DialogDescription className="text-black text-sm pt-1">
            Share your link — you'll earn <b>+10 spots</b> for every friend who <b>signs up</b> with it! 🐆
          </DialogDescription>
        </DialogHeader>
        <div className="bg-white border-2 border-black rounded-xl p-3 text-sm text-black/80 italic">{MESSAGE}</div>
        <div className="flex gap-2">
          <Input data-testid="invite-link" readOnly value={link} className="border-2 border-black text-xs" onFocus={(e) => e.target.select()} />
          <Button data-testid="invite-copy" onClick={copy} className="bg-spotter-maroon text-spotter-cream border-2 border-black shrink-0">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="invite-share" onClick={nativeShare} className="bg-spotter-gold text-spotter-maroon border-2 border-black font-bold gap-1"><Send className="w-4 h-4" />Share…</Button>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(link)}&quote=${enc(MESSAGE)}`} target="_blank" rel="noopener noreferrer" data-testid="invite-facebook"
            className="flex items-center justify-center gap-1 bg-[#1877F2] text-white border-2 border-black rounded-md font-bold text-sm"><Facebook className="w-4 h-4" />Facebook</a>
          <a href={`sms:?&body=${enc(full)}`} data-testid="invite-sms" className="flex items-center justify-center gap-1 bg-white text-spotter-maroon border-2 border-black rounded-md font-bold text-sm py-2">Text message</a>
          <a href={`mailto:?subject=${enc("Join me on Spotter")}&body=${enc(full)}`} data-testid="invite-email" className="flex items-center justify-center gap-1 bg-white text-spotter-maroon border-2 border-black rounded-md font-bold text-sm py-2">Email</a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InviteButton({ className = "", label = "Invite Friends", testid = "invite-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid={testid} onClick={() => setOpen(true)} className={`bg-spotter-maroon text-spotter-cream border-2 border-black font-heading font-bold gap-2 ${className}`}>
        <UserPlus className="w-4 h-4" />{label}
      </Button>
      <InviteDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
